import EditIcon from '@mui/icons-material/Edit';
import { Chip, IconButton, Tooltip, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import useBoolean from 'hooks/useBoolean';
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';
import AuthorEditor from './AuthorEditor';

const AbstractAuthor = forwardRef(
    (
        {
            submission_id,
            disabledButton,
            disabledEdit,
            data,
            handleChange,
            ...other
        },
        ref,
    ) => {
        const [AuthorEditorKey, setAuthorEditorKey] = React.useState(0);
        const [authorID, setAuthorID] = useState('');

        useEffect(() => {}, [data]);

        const openEditor = useBoolean();

        const handleOpenEditor = () => {
            setAuthorTableKey(authorTableKey + 1);
        };

        const [authorTableKey, setAuthorTableKey] = useState(0);
        const closeEditor = () => {
            openEditor.onFalse();
            setAuthorTableKey((authorTableKey) => authorTableKey + 1);
            handleChange();
        };

        useImperativeHandle(ref, () => ({
            openAddAuthor: () => {
                setAuthorEditorKey(AuthorEditorKey + 1);
                openEditor.onTrue();
            },
        }));

        return (
            <TableContainer component={Paper}>
                <AuthorEditor
                    key={AuthorEditorKey}
                    submission_id={submission_id}
                    id={authorID}
                    open={openEditor.value}
                    handleOpen={handleOpenEditor}
                    handleClose={closeEditor}
                />

                <Table
                    key={'authortable' + authorTableKey}
                    size="small"
                    sx={{
                        [`& .${tableCellClasses.root}`]: {
                            borderBottom: '1px solid rgba(0,0,0,0.1)',
                        },
                    }}
                    aria-label="authors table"
                >
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    No
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Author Name
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Institution
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    City, Country
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Phone Number
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    E-mail
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Address
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Corresponding
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Presenter
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    sx={{
                                        fontSize: '0.85em',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    Action
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {(data || [])
                            .slice()
                            .sort(function (a, b) {
                                var keyA = new Date(a.created_at),
                                    keyB = new Date(b.created_at);
                                if (keyA < keyB) return -1;
                                if (keyA > keyB) return 1;
                                return 0;
                            })
                            .map((row, index) => (
                                <TableRow key={row.id}>
                                    <TableCell align="left">
                                        <Typography noWrap>
                                            {index + 1}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        <Typography
                                            sx={{
                                                textDecoration:
                                                    index === 0
                                                        ? 'underline'
                                                        : 'none',
                                            }}
                                        >
                                            {row.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="left">
                                        {row.institution}
                                    </TableCell>
                                    <TableCell align="left">
                                        {row.city}, {row.country.name}
                                    </TableCell>
                                    <TableCell align="left">
                                        {row.phone_number}
                                    </TableCell>
                                    <TableCell align="left">
                                        {row.email}
                                    </TableCell>
                                    <TableCell align="left">
                                        {row.address
                                            ? row.address +
                                              ', ' +
                                              row.postal_code
                                            : ''}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={
                                                row.isCorresponding
                                                    ? 'Yes'
                                                    : 'No'
                                            }
                                            color={
                                                row.isCorresponding
                                                    ? 'success'
                                                    : 'default'
                                            }
                                            size="small"
                                            variant={
                                                row.isCorresponding
                                                    ? 'filled'
                                                    : 'outlined'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={
                                                row.isPresenter ? 'Yes' : 'No'
                                            }
                                            color={
                                                row.isPresenter
                                                    ? 'primary'
                                                    : 'default'
                                            }
                                            size="small"
                                            variant={
                                                row.isPresenter
                                                    ? 'filled'
                                                    : 'outlined'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Edit">
                                            <span>
                                                <IconButton
                                                    disabled={disabledEdit}
                                                    size="small"
                                                    onClick={async () => {
                                                        setAuthorID(row.id);
                                                        setAuthorEditorKey(
                                                            AuthorEditorKey + 1,
                                                        );
                                                        setTimeout(function () {
                                                            openEditor.onTrue();
                                                        }, 200);
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    },
);

export default AbstractAuthor;
